import { ADMIN_API_URL, ADMIN_APP_ID, AUTH_URL } from './apiConfig';
import { fetchJsonWithAuth } from './authApi';

// ADMIN_API_URL already includes the API's version prefix (e.g. "http://host:9008/v1"),
// so admin-scoped routes only need "/admin" appended — Be_Auth_DevOps mounts them at
// "/v1/admin/...", not "/v1/api/v1/admin/...".
const ADMIN_PREFIX = `${ADMIN_API_URL}/admin`;
const AUTH_ADMIN_PREFIX = `${AUTH_URL || ADMIN_API_URL}/admin`;
// Top-level (non "/admin") routes such as /get-all-users, /get-roles, /get-role-details.
const ROOT_PREFIX = ADMIN_API_URL;

async function request(path, options = {}, baseUrl = ADMIN_PREFIX) {
  const json = await fetchJsonWithAuth(baseUrl, path, {
    ...options,
    headers: {
      'X-App-Id': ADMIN_APP_ID,
      ...(options.headers ?? {}),
    },
  });
  return json?.data ?? json;
}

function authAdminRequest(path, options = {}) {
  return request(path, options, AUTH_ADMIN_PREFIX);
}

function rootRequest(path, options = {}) {
  return request(path, options, ROOT_PREFIX);
}

function get(path) {
  return request(path);
}

function post(path, body) {
  return request(path, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

function put(path, body) {
  return request(path, {
    method: 'PUT',
    body: JSON.stringify(body ?? {}),
  });
}

function del(path) {
  return request(path, { method: 'DELETE' });
}

function rootGet(path) {
  return rootRequest(path);
}

function rootPut(path, body) {
  return rootRequest(path, {
    method: 'PUT',
    body: JSON.stringify(body ?? {}),
  });
}

// The Users/Roles field-config editors work off 3 fixed module tabs — the
// backend's /admin/field-config only accepts one ?module= per call, so the
// "all modules at once" shape the UI wants is assembled client-side.
const CONFIG_MODULE_KEYS = ['job', 'candidate', 'submissions'];

export function getAllUsers(params = {}) {
  const { offset, limit, searchQuery, ...rest } = params;
  const query = new URLSearchParams();
  query.set('limit', limit ?? 20);
  query.set('offset', offset ?? 0);
  if (searchQuery) query.set('searchquery', searchQuery);
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, value);
    }
  });
  return rootGet(`/get-all-users?${query}`).then((data) => ({
    users: data?.users ?? [],
    total: data?.totalCount ?? data?.total ?? (data?.users ?? []).length,
  }));
}

export function getUserId(user) {
  return user?.id ?? user?.userId ?? user?.user_id ?? user?.USER_ID;
}

export function getAllTeams() {
  return rootGet('/get-teams');
}

export function updateTeam(team, payload) {
  const id = team?.id ?? team?.teamId ?? team?.team_id ?? team;
  return rootRequest(`/team/edit?teamId=${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(payload ?? {}),
  });
}

// ResponseRole (Go) uses explicit snake_case json tags: role_id, role_name,
// role_description, role_type, user_count — not the ROLENAME-style casing
// used elsewhere in this API, so alias those exact keys here.
function normalizeRoleRecord(role) {
  return {
    ...role,
    roleId: role.role_id ?? role.roleId ?? role.ROLEID ?? role.id,
    roleName: role.role_name ?? role.roleName ?? role.ROLENAME,
    description: role.role_description ?? role.description ?? role.ROLEDESCRIPTION ?? '',
    userCount: role.user_count ?? role.userCount ?? role.USERCOUNT ?? 0,
  };
}

export function getAllRoles() {
  return rootGet('/get-roles').then((data) => {
    const combined = [
      ...(data?.custom_roles ?? data?.CUSTOM_ROLES ?? []),
      ...(data?.zinnext_default ?? data?.ZINNEXT_DEFAULT ?? []),
    ];
    const roles = combined.map(normalizeRoleRecord);
    return { roles, total: roles.length };
  });
}

export function getRoleId(role) {
  return role?.roleId ?? role?.role_id ?? role?.ROLEID ?? role?.id ?? role;
}

// ── Menu / permission map (shared by user- and role-level "Permissions") ──────
// Backed by the same MENU + PERMISSIONKEY records the legacy Role Config screen
// uses (GET /get-role-details, PUT /edit-role-details) — reshaped into the
// { [moduleName]: { permissions: { [actionKey]: { value } } } } map the
// Configuration Center's per-user/per-role permission editors expect.
function normalizeMenuPermissionMap(raw) {
  const groups = Array.isArray(raw)
    ? raw
    : Object.entries(raw ?? {}).map(([name, group]) => ({ ...group, menu_name: name }));

  const map = {};
  groups.forEach((group) => {
    const menuId = group.menu_id ?? group.menuId ?? group.MenuID;
    const menuName = group.menu_name ?? group.menuName ?? group.MenuName ?? String(menuId ?? '');
    const permissions = {};
    Object.entries(group.permissions ?? group.Permissions ?? {}).forEach(([key, perm]) => {
      const permId = perm?.permission_id ?? perm?.permissionId ?? perm?.PermissionID;
      const permKey = perm?.permission_key ?? perm?.permissionKey ?? perm?.PermissionKey ?? key;
      const rawValue = perm?.value ?? perm?.Value ?? perm?.access_value ?? perm;
      permissions[permKey] = { id: permId, value: String(Number(rawValue) === 1 ? 1 : 0) };
    });
    map[menuName] = { menuId, permissions };
  });
  return map;
}

function toMenuPermissionUpdatePayload(rowsMap) {
  const menus = {};
  Object.entries(rowsMap ?? {}).forEach(([menuName, group]) => {
    menus[menuName] = {
      menuId: group.menuId,
      permissions: Object.fromEntries(
        Object.entries(group.permissions ?? {}).map(([key, p]) => [key, { id: p.id, value: String(p.value) }]),
      ),
    };
  });
  return menus;
}

export function getRolePermissions(role) {
  return rootGet(`/get-role-details?roleid=${encodeURIComponent(getRoleId(role))}`).then(normalizeMenuPermissionMap);
}

export function updateRolePermissions(role, permissions) {
  return rootPut('/edit-role-details', { roleId: getRoleId(role), menus: toMenuPermissionUpdatePayload(permissions) });
}

export function getUserPermissions(user) {
  const userId = getUserId(user);
  return rootGet(`/get-role-details?userId=${encodeURIComponent(userId)}`).then(normalizeMenuPermissionMap);
}

export function updateUserPermissions(user, permissions) {
  const userId = getUserId(user);
  return rootPut('/edit-role-details', { userId, menus: toMenuPermissionUpdatePayload(permissions) });
}

// ── Module field config (list view / filter / form visibility) ───────────────
// Backed by GET/PUT /admin/field-config (+ /role, /team variants). The backend
// uses { field, label, ismandatory } — normalize to the fieldKey/fieldName
// aliases the admin UI's field editors read, and back again on save.
function normalizeFieldVisibility(f) {
  return {
    ...f,
    field: f.field ?? f.fieldKey,
    fieldKey: f.fieldKey ?? f.field,
    label: f.label ?? f.fieldName,
    fieldName: f.fieldName ?? f.label,
    ismandatory: f.ismandatory ?? f.isMandatory ?? false,
    isMandatory: f.ismandatory ?? f.isMandatory ?? false,
  };
}

function toFieldVisibilityPayload(f) {
  return {
    ...f,
    field: f.field ?? f.fieldKey,
    label: f.label ?? f.fieldName,
    ismandatory: f.ismandatory ?? f.isMandatory ?? false,
  };
}

function fetchModuleFieldsByScope(scopeParam, scopeValue, configType) {
  return Promise.all(
    CONFIG_MODULE_KEYS.map((mod) => {
      const query = new URLSearchParams({ module: mod, configType: configType ?? '', [scopeParam]: scopeValue ?? '' });
      return get(`/field-config?${query}`)
        .then((fields) => [mod, (fields ?? []).map(normalizeFieldVisibility)])
        .catch(() => [mod, []]);
    }),
  ).then((entries) => Object.fromEntries(entries));
}

// filter-dropdown-fields is a top-level (non "/admin") route that resolves a
// module's available field keys — from its form-group config, falling back to
// scanning the live collection. Returns [{ value, label, type, group }] —
// alias value→field/fieldKey/key so every consumer's preferred key name works.
export function getModuleFields(moduleKey) {
  const query = moduleKey ? `?module=${encodeURIComponent(moduleKey)}` : '';
  return rootGet(`/filter-dropdown-fields${query}`).then((fields) => (fields ?? []).map((f) => ({
    ...f,
    value: f.value ?? f.field ?? f.fieldKey ?? f.key,
    field: f.field ?? f.value ?? f.fieldKey ?? f.key,
    fieldKey: f.fieldKey ?? f.value ?? f.field ?? f.key,
    key: f.key ?? f.value ?? f.field ?? f.fieldKey,
    label: f.label ?? f.fieldName ?? f.name,
    fieldName: f.fieldName ?? f.label ?? f.name,
  })));
}

export function getUserModuleFields(user, configType) {
  return fetchModuleFieldsByScope('userId', getUserId(user), configType);
}

export function updateUserModuleFieldConfig(user, moduleKey, fields, configType) {
  return put('/field-config', {
    module: moduleKey,
    userId: getUserId(user),
    configType,
    visibleFields: (fields ?? []).map(toFieldVisibilityPayload),
  });
}

export function getTeamModuleFields(team, configType) {
  const id = team?.id ?? team?.teamId ?? team?.team_id ?? team;
  return fetchModuleFieldsByScope('teamId', id, configType);
}

export function updateTeamModuleFieldConfig(team, moduleKey, fields, configType) {
  const id = team?.id ?? team?.teamId ?? team?.team_id ?? team;
  return put('/field-config/team', {
    module: moduleKey,
    teamId: id,
    configType,
    visibleFields: (fields ?? []).map(toFieldVisibilityPayload),
  });
}

export function getRoleModuleFields(role, configType) {
  return fetchModuleFieldsByScope('roleId', getRoleId(role), configType);
}

export function updateRoleModuleFieldConfig(role, moduleKey, fields, configType) {
  return put('/field-config/role', {
    module: moduleKey,
    roleId: getRoleId(role),
    configType,
    visibleFields: (fields ?? []).map(toFieldVisibilityPayload),
  });
}

export function getAvailableCollections() {
  return get('/lookup/collections');
}

export function getCollectionFields(collectionName) {
  return get(`/lookup/collection-fields?collection=${encodeURIComponent(collectionName)}`);
}

export function getArraySubfields(moduleKey, fieldName) {
  return get(`/field-array-subfields?module=${encodeURIComponent(moduleKey)}&field=${encodeURIComponent(fieldName)}`)
    .then((r) => r?.fields ?? r ?? []);
}

export function uploadListViewActionIcon(file) {
  const formData = new FormData();
  formData.append('icon', file);
  return request('/field-config/action-icon', {
    method: 'POST',
    body: formData,
    headers: {},
  });
}

export function getRowActionConfigs(moduleKey) {
  const query = moduleKey ? `?module=${encodeURIComponent(moduleKey)}` : '';
  return get(`/row-action-config${query}`);
}

export function createRowActionConfig(payload) {
  return post('/row-action-config', payload);
}

export function updateRowActionConfig(id, payload) {
  return put(`/row-action-config/${id}`, Array.isArray(payload) ? { rowActions: payload } : payload);
}

export function deleteRowActionConfig(id) {
  return del(`/row-action-config/${id}`);
}

export function seedRowActionConfigs() {
  return post('/row-action-config/seed');
}

export function getModuleActionRules(moduleKey) {
  const query = moduleKey ? `?module=${encodeURIComponent(moduleKey)}` : '';
  return get(`/module-action-rules${query}`);
}

export function createModuleActionRule(payload) {
  return post('/module-action-rules', payload);
}

export function updateModuleActionRule(id, payload) {
  return put(`/module-action-rules/${id}`, payload);
}

export function deleteModuleActionRule(id) {
  return del(`/module-action-rules/${id}`);
}

// GET /admin/form-groups returns two different shapes depending on ?module=:
// with a module it's the single FormGroupConfig document ({..., groups: [...]});
// without one (merge-all) it's already a flat array of groups. Always resolve
// to the flat array the admin UI's tables expect.
export function getAdminFormGroups(moduleKey = '') {
  const query = moduleKey ? `?module=${encodeURIComponent(moduleKey)}` : '';
  return get(`/form-groups${query}`).then((data) => (
    Array.isArray(data) ? data : (data?.groups ?? [])
  ));
}

export function createAdminFormGroup(payload) {
  return post('/form-groups', payload);
}

export function updateAdminFormGroup(id, payload) {
  return put(`/form-groups/${id}`, payload);
}

export function deleteAdminFormGroup(id) {
  return del(`/form-groups/${id}`);
}

export function seedAdminFormGroups() {
  return post('/form-groups/seed');
}

export function getCustomForms() {
  return get('/custom-forms');
}

export function createCustomForm(payload) {
  return post('/custom-forms', payload);
}

export function updateCustomForm(id, payload) {
  return put(`/custom-forms/${id}`, payload);
}

export function deleteCustomForm(id) {
  return del(`/custom-forms/${id}`);
}

export function getDetailConfig(moduleKey) {
  return get(`/detail-config?module=${encodeURIComponent(moduleKey)}`);
}

export function saveDetailConfig(moduleKey, payload) {
  return put('/detail-config', { ...payload, module: moduleKey });
}

// No backend route exists yet for a per-record detail-view preview (the
// closest handler, JobDetailedViewHandler, renders a job-specific detailed
// view from a different collection and doesn't return {groups} shaped data).
// Surface a clear error instead of silently hitting the wrong endpoint.
export function getModuleDetailPreview() {
  return Promise.reject(new Error('Detail preview is not available yet — no backend endpoint implements it.'));
}

export function getDetailDefaults() {
  return get('/detail-defaults');
}

export function saveDetailDefaults(payload) {
  return put('/detail-defaults', payload);
}

export function getModules() {
  return get('/modules');
}

function normalizeMenuModule(module) {
  if (!module || typeof module !== 'object') return module;
  const id = module.MENUID ?? module.MENU_ID ?? module.id ?? module.menuId ?? module.legacyMenuId;
  const name = module.MENUNAME ?? module.MENU_NAME ?? module.menuName ?? module.name ?? module.label;
  const key = module.MENUKEY ?? module.MENU_KEY ?? module.menuKey ?? module.key;
  return {
    ...module,
    id,
    MENUID: id,
    MENU_ID: id,
    menuId: id,
    legacyMenuId: module.legacyMenuId ?? id,
    MENUNAME: name,
    MENU_NAME: name,
    menuName: name,
    name: module.name ?? name,
    label: module.label ?? name,
    menuKey: key,
    key,
  };
}

function normalizeMenuAction(action) {
  if (!action || typeof action !== 'object') return action;
  const id = action.PERMISSIONID ?? action.PERMISSION_ID ?? action.id ?? action.permissionId ?? action.legacyPermissionId;
  const name = action.PERMISSIONNAME ?? action.PERMISSION_NAME ?? action.permissionName ?? action.name ?? action.label;
  const key = action.PERMISSIONKEY ?? action.PERMISSION_KEY ?? action.permissionKey ?? action.key;
  return {
    ...action,
    id,
    PERMISSIONID: id,
    PERMISSION_ID: id,
    permissionId: id,
    legacyPermissionId: action.legacyPermissionId ?? id,
    PERMISSIONNAME: name,
    PERMISSION_NAME: name,
    permissionName: name,
    name: action.name ?? name,
    label: action.label ?? name,
    PERMISSIONKEY: key,
    PERMISSION_KEY: key,
    permissionKey: key,
    key,
  };
}

export function getMenuModules() {
  return authAdminRequest('/menu-modules').then((items) => (Array.isArray(items) ? items.map(normalizeMenuModule) : items));
}

export function createMenuModule(payload) {
  return authAdminRequest('/menu-modules', {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
  }).then(normalizeMenuModule);
}

export function updateMenuModule(id, payload) {
  return authAdminRequest(`/menu-modules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload ?? {}),
  });
}

export function deleteMenuModule(id) {
  return authAdminRequest(`/menu-modules/${id}`, { method: 'DELETE' });
}

export function getMenuActions(moduleKey) {
  const query = moduleKey ? `?module=${encodeURIComponent(moduleKey)}` : '';
  return authAdminRequest(`/menu-actions${query}`).then((items) => (Array.isArray(items) ? items.map(normalizeMenuAction) : items));
}

export function createMenuAction(payload) {
  return authAdminRequest('/menu-actions', {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
  }).then(normalizeMenuAction);
}

export function updateMenuAction(id, payload) {
  return authAdminRequest(`/menu-actions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload ?? {}),
  });
}

export function deleteMenuAction(id) {
  return authAdminRequest(`/menu-actions/${id}`, { method: 'DELETE' });
}
